import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import type { Money } from '@/lib/api-admin/generated/types.gen';
import { throwFromResponse } from '../lib/apiErrorMessage';

/** 회원 상세 [주문 이력] 행 — 주문 목록 API(OrderSummary)에서 쓰는 칸만. */
export interface UserOrderItem {
  orderId: string;
  orderNo: string;
  orderStatus: string;
  paymentStatus: string;
  /** 실 결제금액(주문 목록 `paymentAmount`). 응답에 없으면 null. */
  amount: Money | null;
  /** 주문일시(주문 목록 `orderedAt`). */
  orderedAt: string | null;
}

export interface UserOrdersPage {
  items: ReadonlyArray<UserOrderItem>;
  total: number;
}

type Rec = Record<string, unknown>;

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

const asMoney = (v: unknown): Money | null => {
  const m = v as { amount?: unknown; currencyCode?: unknown } | null | undefined;
  return m && typeof m.amount === 'number' && typeof m.currencyCode === 'string'
    ? { amount: m.amount, currencyCode: m.currencyCode }
    : null;
};

const toItem = (raw: Rec): UserOrderItem => ({
  orderId: String(raw.orderId ?? ''),
  orderNo: String(raw.orderNo ?? ''),
  orderStatus: String(raw.orderStatus ?? ''),
  paymentStatus: String(raw.paymentStatus ?? ''),
  amount: asMoney(raw.paymentAmount),
  orderedAt: str(raw.orderedAt) ?? str(raw.createdAt),
});

export const userOrdersQueryKey = (userId: string, page: number) =>
  ['admin', 'user-orders', userId, page] as const;

/**
 * 회원 상세 [주문 이력] — `GET /api/admin/v1/orders?userId=` (order-service #63, 회원 ID 정확 일치, 기간 제한 없음).
 * 종전엔 `q={회원 이름}` 으로 찾아 BE 가 주문자 이름을 검색하지 않아 늘 0건이었고(동명이인 위험도 있었다),
 * 페이지 정보도 `data.meta` 를 읽어 총 건수가 늘 0 이었다 — 주문 목록처럼 최상위 `meta.pagination` 을 읽는다.
 */
export const useUserOrdersQuery = (userId: string, page = 1, size = 10) =>
  useQuery<UserOrdersPage>({
    queryKey: userOrdersQueryKey(userId, page),
    queryFn: async () => {
      const qs = new URLSearchParams({ userId, page: String(Math.max(0, page - 1)), size: String(size) });
      const res = await adminFetch(`/api/admin/v1/orders?${qs.toString()}`);
      if (!res.ok) await throwFromResponse(res, '회원 주문 내역을 불러오지 못했습니다.');
      const env = (await res.json()) as {
        data?: { items?: Rec[] } | Rec[] | null;
        meta?: { pagination?: { totalItems?: number } | null } | null;
      };
      const rawItems = Array.isArray(env.data) ? env.data : (env.data?.items ?? []);
      const items = rawItems.map(toItem);
      return { items, total: env.meta?.pagination?.totalItems ?? items.length };
    },
    enabled: Boolean(userId),
  });
